import { MultiSelect as MantineMultiSelect } from '@mantine/core'
import { keepPreviousData } from '@tanstack/react-query'
import type { FieldProps } from 'formik'
import { Field, Formik } from 'formik'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import styles from '../shared/MultiSelectStyles.module.scss'
import { mapAccountsToSelectOptions, skipClientSideFiltering } from '../shared/accountSelectOptions'

import type { TaskTemplate } from '@percent/admin-dashboard/api/actions/grantProgram/grantProgram.types'
import { useNominatorGroupOptions } from '@percent/admin-dashboard/app/grants/assignmentRules/hooks/useNominatorGroupOptions'
import { useGrantsUtils } from '@percent/admin-dashboard/app/grants/utils/utils'
import { mapCountriesToSelectOptions } from '@percent/admin-dashboard/common/utility/mapCountriesToSelectOptions'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { AcknowledgeModal, ActionModal, Flex, FormField, Modal, MultiSelect, Text, TextInput } from '@percent/lemonade'
import { useMutation, useQuery } from '@percent/utility'
import type { EditAssignmentRuleModalProps } from './EditAssignmentRuleModal.types'

type FormValues = {
  name: string
  countries: string[]
  accountIds: string[]
  taskTemplateIds: string[]
  nominatorGroups: string[]
}

type RuleUpdatePayload = Partial<FormValues>

const toOptionValues = (selectedOptions: Array<{ label: string; value: string }>): string[] =>
  selectedOptions.map(selectedOption => selectedOption.value)

export function EditAssignmentRuleModal({ isOpen, onClose, rule, refresh }: Readonly<EditAssignmentRuleModalProps>) {
  const { t } = useTranslation()
  const { grantProgramService, accountService, countriesService } = useServices()
  const [modalState, setModalState] = useState<'success' | 'error' | null>(null)
  const [accountSearchQuery, setAccountSearchQuery] = useState<string>('')
  const { toAlphabeticalCompare } = useGrantsUtils()

  const countriesQuery = useQuery(['countries'], countriesService.getCountries, {}, { enabled: isOpen })
  const countries = useMemo(() => countriesQuery.data ?? [], [countriesQuery.data])
  const countriesOptions = useMemo(() => mapCountriesToSelectOptions(countries), [countries])

  const partnerId = rule?.partnerId
  const accountsQuery = useQuery(
    ['accounts', 'partner', partnerId || 'none', accountSearchQuery],
    accountService.getAccounts,
    {
      ...(partnerId && { partnerId }),
      ...(accountSearchQuery && { search: accountSearchQuery }),
      pageSize: 1000
    },
    { enabled: isOpen && !!partnerId, placeholderData: keepPreviousData }
  )

  const accountsOptions = useMemo(() => mapAccountsToSelectOptions(accountsQuery.data), [accountsQuery.data])

  const ruleTaskTemplateIds = rule?.taskTemplates?.map(tt => tt.taskTemplateId) ?? []
  const partnerTaskTemplatesQuery = useQuery(
    ['task-templates', 'partner', partnerId || 'none'],
    grantProgramService.getPartnerTaskTemplates,
    { partnerId: partnerId || '' },
    { enabled: isOpen && !!partnerId }
  )

  const programId = useMemo(() => {
    if (!partnerTaskTemplatesQuery.data || ruleTaskTemplateIds.length === 0) {
      return undefined
    }
    const firstMatchingTemplate = partnerTaskTemplatesQuery.data.find(tt => ruleTaskTemplateIds.includes(tt.id))

    return firstMatchingTemplate?.grantProgramId
  }, [partnerTaskTemplatesQuery.data, ruleTaskTemplateIds])

  const { nominatorGroupOptions, isLoading: nominatorGroupsLoading } = useNominatorGroupOptions({
    programId,
    enabled: isOpen && !!programId
  })

  const [allTaskTemplates, setAllTaskTemplates] = useState<TaskTemplate[]>([])
  const [taskTemplatesError, setTaskTemplatesError] = useState<Error | null>(null)

  const fetchTaskTemplates = useCallback(
    (programIdToFetch: string) => {
      if (!programIdToFetch) {
        setAllTaskTemplates([])

        return
      }

      setTaskTemplatesError(null)

      grantProgramService
        .getTaskTemplates({ programId: programIdToFetch })
        .then(result => {
          const templates = (result.data?.data ?? []) as TaskTemplate[]
          setAllTaskTemplates(templates)
        })
        .catch(err => {
          setTaskTemplatesError(err)
        })
    },
    [grantProgramService]
  )

  useEffect(() => {
    if (programId) {
      fetchTaskTemplates(programId)
    } else {
      setAllTaskTemplates([])
    }
  }, [programId, fetchTaskTemplates])

  const taskTemplateOptions = useMemo(() => {
    return allTaskTemplates
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(template => ({
        label: template.name,
        value: template.id
      }))
  }, [allTaskTemplates])

  useEffect(() => {
    if (!isOpen) {
      setAccountSearchQuery('')
      setAllTaskTemplates([])
      setTaskTemplatesError(null)
    }
  }, [isOpen])

  const [{ isPending: isSubmitting, errorMessage }, { apiFunc: updateRule }] = useMutation(
    grantProgramService.updateAutoAssignmentRule,
    () => {
      refresh()
      setModalState('success')
    },
    () => {
      setModalState('error')
    }
  )

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string()
          .required(t('grants.assignmentRules.editModal.validation.name.required'))
          .max(255, t('grants.assignmentRules.editModal.validation.name.maxLength'))
          .trim(),
        countries: Yup.array().of(Yup.string()).nullable().optional().default([]),
        accountIds: Yup.array().of(Yup.string()).nullable().optional().default([]),
        taskTemplateIds: Yup.array()
          .of(Yup.string())
          .min(1, t('errorMessage.required'))
          .required(t('errorMessage.required')),
        nominatorGroups: Yup.array().of(Yup.string()).nullable().optional().default([])
      }),
    [t]
  )

  const initialValues = useMemo<FormValues>(
    () => ({
      name: rule?.name || '',
      countries: rule?.countries.map(c => c.countryCode) ?? [],
      accountIds: rule?.accounts.map(a => a.accountId) ?? [],
      taskTemplateIds: rule?.taskTemplates.map(tt => tt.taskTemplateId) ?? [],
      nominatorGroups: rule?.nominatorGroups?.map(ng => ng.nominatorGroup) ?? []
    }),
    [rule]
  )

  const handleClose = useCallback(() => {
    onClose()
    setModalState(null)
  }, [onClose])

  const getFieldError = (field: string | string[] | undefined): string | undefined => {
    if (typeof field === 'string') return field

    if (Array.isArray(field)) return field.join(', ')

    return undefined
  }

  const getChangedFields = useCallback(
    (values: FormValues): RuleUpdatePayload => {
      if (!rule) return {}

      const sortAlphabetically = (list: string[]) => [...list].sort(toAlphabeticalCompare)
      const holdsSameValues = (list: string[], otherList: string[]) =>
        JSON.stringify(sortAlphabetically(list)) === JSON.stringify(sortAlphabetically(otherList))

      const changedFields: RuleUpdatePayload = {}

      if (values.name.trim() !== rule.name.trim()) {
        changedFields.name = values.name
      }

      if (
        !holdsSameValues(
          values.countries,
          rule.countries.map(country => country.countryCode)
        )
      ) {
        changedFields.countries = sortAlphabetically(
          values.countries.length > 0 ? values.countries : countries.map((country: { code: string }) => country.code)
        )
      }

      if (
        !holdsSameValues(
          values.accountIds,
          rule.accounts.map(account => account.accountId)
        )
      ) {
        changedFields.accountIds = sortAlphabetically(values.accountIds)
      }

      if (
        !holdsSameValues(
          values.taskTemplateIds,
          rule.taskTemplates.map(taskTemplate => taskTemplate.taskTemplateId)
        )
      ) {
        changedFields.taskTemplateIds = sortAlphabetically(values.taskTemplateIds)
      }

      if (
        !holdsSameValues(
          values.nominatorGroups,
          rule.nominatorGroups?.map(nominatorGroup => nominatorGroup.nominatorGroup) ?? []
        )
      ) {
        changedFields.nominatorGroups = sortAlphabetically(values.nominatorGroups)
      }

      return changedFields
    },
    [rule, countries, toAlphabeticalCompare]
  )

  const handleSubmit = useCallback(
    (values: FormValues) => {
      if (!rule) return

      const payload = getChangedFields(values)

      if (Object.keys(payload).length === 0) {
        return
      }

      updateRule({
        id: rule.id,
        payload
      })
    },
    [rule, updateRule, getChangedFields]
  )

  const renderModalContent = () => {
    if (modalState === 'success') {
      return (
        <AcknowledgeModal
          result="positive"
          title={t('grants.assignmentRules.editModal.success.title')}
          buttonText={t('button.close')}
          handleClose={handleClose}
          viewTestId="edit-assignment-rule-success-modal"
        >
          <Text size="small">{t('grants.assignmentRules.editModal.success.description')}</Text>
        </AcknowledgeModal>
      )
    }

    if (modalState === 'error') {
      return (
        <AcknowledgeModal
          result="negative"
          title={t('grants.assignmentRules.editModal.error.title')}
          description={errorMessage || t('grants.assignmentRules.editModal.error.description')}
          buttonText={t('button.close')}
          handleClose={handleClose}
          viewTestId="edit-assignment-rule-error-modal"
        />
      )
    }

    return (
      <Formik<FormValues>
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {formik => {
          const hasChanges = Object.keys(getChangedFields(formik.values)).length > 0

          const selectedCountries = countriesOptions.filter(c => formik.values.countries.includes(c.value))
          const selectedTaskTemplates = taskTemplateOptions.filter(tt =>
            formik.values.taskTemplateIds.includes(tt.value)
          )

          return (
            <ActionModal
              title={t('grants.assignmentRules.editModal.title')}
              primaryButtonText={t('button.save')}
              secondaryButtonText={t('button.cancel')}
              handleClose={handleClose}
              handleSubmit={formik.submitForm}
              loading={
                isSubmitting ||
                countriesQuery.isLoading ||
                partnerTaskTemplatesQuery.isLoading ||
                taskTemplatesError !== null
              }
              disabled={!formik.isValid || !hasChanges || isSubmitting}
              primaryBtnTestId="edit-assignment-rule-submit-button"
              secondaryBtnTestId="edit-assignment-rule-cancel-button"
            >
              <Flex direction="column" gap="lg">
                <Field name="name">
                  {({ field, meta }: FieldProps<string>) => (
                    <FormField
                      label={t('grants.assignmentRules.editModal.fields.name.label')}
                      status={meta.touched && meta.error ? 'danger' : 'default'}
                      statusMessage={meta.touched && meta.error ? meta.error : undefined}
                      necessity="required"
                      data-testid="assignmentRuleName"
                    >
                      <TextInput
                        placeholder={t('grants.assignmentRules.editModal.fields.name.placeholder')}
                        name={field.name}
                        value={field.value || ''}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        status={meta.touched && meta.error ? 'danger' : 'default'}
                      />
                    </FormField>
                  )}
                </Field>

                <Field name="countries">
                  {({ meta }: FieldProps<string[]>) => (
                    <FormField
                      label={t('grants.assignmentRules.editModal.fields.countries.label')}
                      status={meta.touched && meta.error ? 'danger' : 'default'}
                      statusMessage={meta.touched && meta.error ? meta.error : undefined}
                      necessity="optional"
                      data-testid="assignmentRuleCountries"
                    >
                      <MultiSelect
                        placeholder={t('grants.assignmentRules.editModal.fields.countries.placeholder')}
                        options={countriesOptions ?? []}
                        defaultValue={selectedCountries}
                        onChange={(
                          selectedOptions: Array<{
                            label: string
                            value: string
                          }>
                        ) => {
                          formik.setFieldValue('countries', toOptionValues(selectedOptions))
                        }}
                      />
                    </FormField>
                  )}
                </Field>

                <Field name="taskTemplateIds">
                  {({ meta }: FieldProps<string[]>) => (
                    <FormField
                      label={t('tasksAutoAssignmentRule.form.taskTemplateId.label')}
                      data-testid="assignmentRuleTaskTemplates"
                      status={meta.touched && meta.error ? 'danger' : 'default'}
                      statusMessage={getFieldError(meta.error)}
                      necessity="required"
                      disabled={!programId || taskTemplatesError !== null}
                    >
                      <MultiSelect
                        key={`taskTemplates-${programId}`}
                        placeholder={t('tasksAutoAssignmentRule.form.taskTemplateId.placeholder')}
                        defaultValue={selectedTaskTemplates}
                        options={taskTemplateOptions ?? []}
                        disabled={!programId || taskTemplatesError !== null}
                        onChange={(
                          selectedOptions: Array<{
                            label: string
                            value: string
                          }>
                        ) => {
                          formik.setFieldValue('taskTemplateIds', toOptionValues(selectedOptions))
                        }}
                      />
                    </FormField>
                  )}
                </Field>

                <Field name="nominatorGroups">
                  {({ meta }: FieldProps<string[]>) => {
                    const selectedNominatorGroups = nominatorGroupOptions.filter(ng =>
                      formik.values.nominatorGroups.includes(ng.value)
                    )

                    return (
                      <FormField
                        label={t('tasksAutoAssignmentRule.form.nominatorGroups.label')}
                        data-testid="assignmentRuleNominatorGroups"
                        status={meta.touched && meta.error ? 'danger' : 'default'}
                        statusMessage={meta.touched && meta.error ? meta.error : undefined}
                        necessity="optional"
                        disabled={!programId || nominatorGroupsLoading}
                      >
                        <MultiSelect
                          key={`nominatorGroups-${programId}`}
                          placeholder={t('tasksAutoAssignmentRule.form.nominatorGroups.placeholder')}
                          defaultValue={selectedNominatorGroups}
                          options={nominatorGroupOptions}
                          disabled={!programId || nominatorGroupsLoading}
                          onChange={(
                            selectedOptions: Array<{
                              label: string
                              value: string
                            }>
                          ) => {
                            formik.setFieldValue('nominatorGroups', toOptionValues(selectedOptions))
                          }}
                        />
                      </FormField>
                    )
                  }}
                </Field>

                <Field name="accountIds">
                  {({ meta }: FieldProps<string[]>) => (
                    <FormField
                      label={t('grants.assignmentRules.editModal.fields.accounts.label')}
                      data-testid="assignmentRuleAccounts"
                      status={meta.touched && meta.error ? 'danger' : 'default'}
                      statusMessage={meta.touched && meta.error ? meta.error : undefined}
                      necessity="optional"
                      disabled={!partnerId || accountsQuery.isLoading}
                    >
                      <MantineMultiSelect
                        placeholder={t('grants.assignmentRules.editModal.fields.accounts.placeholder')}
                        data={accountsOptions ?? []}
                        value={formik.values.accountIds}
                        disabled={!partnerId || accountsQuery.isLoading}
                        onChange={selectedValues => {
                          formik.setFieldValue('accountIds', selectedValues)
                        }}
                        searchable
                        filter={skipClientSideFiltering}
                        onSearchChange={searchValue => {
                          setAccountSearchQuery(searchValue)
                        }}
                        nothingFoundMessage={t('typography.noResultsFound')}
                        comboboxProps={{ withinPortal: false }}
                        className={styles.customMultiSelect}
                      />
                    </FormField>
                  )}
                </Field>
              </Flex>
            </ActionModal>
          )
        }}
      </Formik>
    )
  }

  return (
    <Modal open={isOpen} onClose={handleClose} aria-labelledby="edit-assignment-rule-modal">
      {renderModalContent()}
    </Modal>
  )
}
